import { ApolloError } from '@apollo/client';
import _ from 'lodash';
import type { GraphQLFormattedError } from 'graphql';
import { UserVisibleError } from './types';

const defaultErrorMessage = 'Something went wrong. Please try again.';

const getUserVisibleError = (errors: readonly GraphQLFormattedError[]): string | null => {
  if (!errors.length) {
    return null;
  }

  return (
    errors
      .filter((error) => !!error.extensions?.exception?.userVisibleError)
      .map((error) => {
        /* eslint-disable @typescript-eslint/no-non-null-assertion */
        const visibleError = error!.extensions!.exception!.userVisibleError as UserVisibleError;
        return `${visibleError.message} ${visibleError!.details}`;
        /* eslint-enable @typescript-eslint/no-non-null-assertion */
      })
      .join('\n') || null
  );
};

const getErrorMessageFromApolloError = ({
  graphQLErrors,
  networkError,
  message,
}: Partial<ApolloError>): string | undefined => {
  if (!graphQLErrors?.length && !networkError) {
    return message;
  }

  const userVisibleError = graphQLErrors && getUserVisibleError(graphQLErrors);

  if (userVisibleError) {
    return userVisibleError;
  }

  const graphQLErrorMessage = _.map(graphQLErrors, (error) => {
    return error.message;
  }).join('\n');
  const networkErrorArray = _.get(networkError, 'result.errors', []);
  const networkErrorMessages = _.map(networkErrorArray, 'message').join('\n');
  const networkErrorMessage = _.get(networkError, 'message');

  return graphQLErrorMessage || networkErrorMessages || networkErrorMessage;
};

const getErrorMessageFromApolloErrors = (errors: ReadonlyArray<GraphQLFormattedError>): string =>
  errors.reduce((acc, error) => {
    const errorMessage = getErrorMessageFromApolloError(error);
    return acc ? `${acc}\n${errorMessage}` : errorMessage || defaultErrorMessage;
  }, '');

const isGraphQLErrorArray = (
  error: ApolloError | Error | ReadonlyArray<GraphQLFormattedError>,
): error is ReadonlyArray<GraphQLFormattedError> => {
  return Array.isArray(error);
};

export const getErrorMessage = (
  error: ApolloError | Error | ReadonlyArray<GraphQLFormattedError>,
): string => {
  if (isGraphQLErrorArray(error)) {
    return getErrorMessageFromApolloErrors(error) || defaultErrorMessage;
  }

  return getErrorMessageFromApolloError(error) || defaultErrorMessage;
};
